import React, {useState, useEffect, useRef} from 'react';
import Icon from './icons/Icon';
import './DynamicTaskPanel.css';

/**
 * DynamicTaskPanel Component
 * 
 * Displays project status and actionable task cards that reflect the actual state:
 * - Project created/not created
 * - Services running/stopped
 * - Deployments pending
 * - Errors detected
 * 
 * This provides Lovable-style UX with dynamic, contextual tasks.
 */
const DynamicTaskPanel = ({
  selectedProject,
  previewTabs = [],
  configuration,
  selectedEnvironment,
  onAction,
  isVisible = true
}) => {
  const [tasks, setTasks] = useState([]);
  const [isMinimized, setIsMinimized] = useState(false);
  
  // Use refs to track previous values and prevent unnecessary updates
  const prevDepsRef = useRef();

  // Generate dynamic tasks based on project state
  useEffect(() => {
    // Create a stable comparison string
    const currentDeps = JSON.stringify({
      projectId: selectedProject?.id,
      projectName: selectedProject?.name,
      previewTabsCount: previewTabs.length,
      previewTabsState: previewTabs.map(t => ({name: t.serviceName, url: !!t.url, error: !!t.error, loading: t.loading})),
      servicesCount: configuration?.services ? Object.keys(configuration.services).length : 0,
      environmentId: selectedEnvironment?.envId
    });
    
    // Only update if dependencies actually changed
    if (prevDepsRef.current === currentDeps) {
      return;
    }
    prevDepsRef.current = currentDeps;

    const generatedTasks = [];

    // Task 1: Project Status
    if (selectedProject) {
      generatedTasks.push({
        id: 'project-status',
        title: `Project: ${selectedProject.name}`,
        status: 'completed',
        icon: '✅',
        type: 'info'
      });
    } else {
      generatedTasks.push({
        id: 'project-needed',
        title: 'Select or create a project',
        status: 'not-started',
        icon: '📦',
        type: 'action',
        action: () => onAction?.('create-project')
      });
    }

    // Task 2: Services Status
    if (previewTabs.length > 0) {
      const runningServices = previewTabs.filter(tab => tab.url);
      const failedServices = previewTabs.filter(tab => tab.error);
      const startingServices = previewTabs.filter(tab => tab.loading);

      if (runningServices.length > 0) {
        generatedTasks.push({
          id: 'services-running',
          title: `${runningServices.length} service${runningServices.length > 1 ? 's' : ''} running`,
          status: 'completed',
          icon: '🟢',
          type: 'info',
          details: runningServices.map(s => s.serviceName).join(', ')
        });
      }

      if (startingServices.length > 0) {
        generatedTasks.push({
          id: 'services-starting',
          title: `${startingServices.length} service${startingServices.length > 1 ? 's' : ''} starting...`,
          status: 'in-progress',
          icon: '🟡',
          type: 'info',
          details: startingServices.map(s => s.serviceName).join(', ')
        });
      }

      if (failedServices.length > 0) {
        generatedTasks.push({
          id: 'services-error',
          title: `${failedServices.length} service${failedServices.length > 1 ? 's' : ''} failed`,
          status: 'error',
          icon: '⚠️',
          type: 'action',
          details: failedServices[0].error?.substring(0, 60),
          action: () => onAction?.('view-logs', failedServices[0])
        });
      }

      if (runningServices.length === 0 && startingServices.length === 0) {
        generatedTasks.push({
          id: 'services-stopped',
          title: 'No services running',
          status: 'not-started',
          icon: '⚪',
          type: 'action',
          action: () => onAction?.('start-service')
        });
      }
    } else if (configuration?.services && Object.keys(configuration.services).length > 0) {
      generatedTasks.push({
        id: 'services-available',
        title: 'Start a service to preview',
        status: 'not-started',
        icon: '🚀',
        type: 'action',
        action: () => onAction?.('start-service')
      });
    }

    // Task 3: Deployment Status
    if (selectedEnvironment) {
      generatedTasks.push({
        id: 'deployment',
        title: `Deploy to ${selectedEnvironment.label || selectedEnvironment.envId}`,
        status: 'not-started',
        icon: '🚀',
        type: 'action',
        action: () => onAction?.('deploy')
      });
    }

    // Task 4: Configuration Status
    if (configuration) {
      const serviceCount = configuration.services ? Object.keys(configuration.services).length : 0;
      const endpointCount = configuration.endpoints ? Object.keys(configuration.endpoints).length : 0;
      const collectionCount = configuration.collections ? Object.keys(configuration.collections).length : 0;

      if (serviceCount === 0) {
        generatedTasks.push({
          id: 'add-service',
          title: 'Add a service (backend or frontend)',
          status: 'not-started',
          icon: '➕',
          type: 'action',
          action: () => onAction?.('add-service')
        });
      }

      if (serviceCount > 0 && endpointCount === 0) {
        generatedTasks.push({
          id: 'add-endpoint',
          title: 'Create API endpoints',
          status: 'not-started',
          icon: '🔌',
          type: 'action',
          action: () => onAction?.('add-endpoint')
        });
      }
    }

    setTasks(generatedTasks);
  }, [selectedProject, previewTabs, configuration, selectedEnvironment]);
  // Note: onAction is intentionally excluded from dependencies to prevent infinite loops
  // It's only used in action callbacks, not directly in the effect

  if (!isVisible) {
    return null;
  }

  if (isMinimized) {
    return (
      <div className="dynamic-task-panel minimized">
        <button
          className="minimize-btn"
          onClick={() => setIsMinimized(false)}
          title="Expand Tasks"
          type="button"
        >
          <Icon name="chevron-left" size={16} />
        </button>
        <div className="minimized-indicator">
          <span className="task-icon">📋</span>
          <span className="task-count">{tasks.length}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="dynamic-task-panel">
      <div className="task-panel-header">
        <h3 className="panel-title">
          <Icon name="list" size={18} />
          {selectedProject ? selectedProject.name : 'Current Project'}
        </h3>
        <button
          className="minimize-btn"
          onClick={() => setIsMinimized(true)}
          title="Minimize"
          type="button"
        >
          <Icon name="chevron-right" size={16} />
        </button>
      </div>

      <div className="task-list">
        {tasks.map((task) => (
          <div
            key={task.id}
            className={`task-card ${task.type} ${task.status}`}
            onClick={task.action}
            role={task.action ? 'button' : undefined}
            tabIndex={task.action ? 0 : undefined}
          >
            <div className="task-icon-wrapper">
              <span className="task-icon">{task.icon}</span>
            </div>
            <div className="task-content">
              <div className="task-title">{task.title}</div>
              {task.details && (
                <div className="task-details">{task.details}</div>
              )}
            </div>
            {task.action && (
              <div className="task-action-indicator">
                <Icon name="chevron-right" size={14} />
              </div>
            )}
          </div>
        ))}
      </div>

      {tasks.length === 0 && (
        <div className="empty-state">
          <Icon name="check-circle" size={32} />
          <p>All set!</p>
        </div>
      )}
    </div>
  );
};

export default DynamicTaskPanel;
